import { cookieService } from "./cookieService";
import Cookies from "js-cookie";

// סוגי קוקיז לפי GDPR
export type CookieCategory =
  | "essential"
  | "functional"
  | "analytics"
  | "marketing";

// מצב הסכמה
export interface CookieConsent {
  essential: boolean; // תמיד true - חובה לתפקוד האתר
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
}

// הגדרות קוקיז
export interface CookieSettings {
  name: string;
  category: CookieCategory;
  description: string;
  purpose: string;
  retention: string;
  required: boolean;
}

// רשימת כל הקוקיז באתר
export const COOKIE_DEFINITIONS: CookieSettings[] = [
  // קוקיז חיוניים - תמיד מופעלים
  {
    name: "cookie_consent",
    category: "essential",
    description: "הסכמה לשימוש בקוקיז",
    purpose: "שמירת העדפות המשתמש לגבי שימוש בקוקיז",
    retention: "365 ימים",
    required: true,
  },
  {
    name: "auth_token",
    category: "essential",
    description: "טוקן אימות משתמש",
    purpose: "שמירת מצב התחברות",
    retention: "24 שעות",
    required: true,
  },
  {
    name: "session_id",
    category: "essential",
    description: "מזהה הפעלה",
    purpose: "זיהוי הפעלה נוכחית",
    retention: "עד לסגירת הדפדפן",
    required: true,
  },
  {
    name: "csrf_token",
    category: "essential",
    description: "טוקן אבטחה",
    purpose: "מניעת התקפות CSRF",
    retention: "עד לסגירת הדפדפן",
    required: true,
  },

  // קוקיז פונקציונליים
  {
    name: "user_preferences",
    category: "functional",
    description: "העדפות משתמש",
    purpose: "שמירת העדפות שפה, ערכת נושא ונגישות",
    retention: "365 ימים",
    required: false,
  },
  {
    name: "shopping_cart",
    category: "functional",
    description: "עגלת קניות",
    purpose: "שמירת פריטים בעגלת הקניות",
    retention: "7 ימים",
    required: false,
  },
  {
    name: "search_history",
    category: "functional",
    description: "היסטוריית חיפוש",
    purpose: "שמירת חיפושים אחרונים לשיפור חוויית המשתמש",
    retention: "30 ימים",
    required: false,
  },
  {
    name: "last_visit",
    category: "functional",
    description: "זמן ביקור אחרון",
    purpose: "זיהוי ביקור ראשון או חוזר",
    retention: "365 ימים",
    required: false,
  },
  {
    name: "accessibility_preferences",
    category: "functional",
    description: "העדפות נגישות",
    purpose: "שמירת הגדרות נגישות אישיות",
    retention: "365 ימים",
    required: false,
  },
  {
    name: "notifications_enabled",
    category: "functional",
    description: "העדפות התראות",
    purpose: "שמירת העדפות התראות",
    retention: "365 ימים",
    required: false,
  },
  {
    name: "preferred_language",
    category: "functional",
    description: "שפה מועדפת",
    purpose: "שמירת השפה שנבחרה על ידי המשתמש",
    retention: "365 ימים",
    required: false,
  },

  // קוקיז אנליטיים
  {
    name: "api_workshops",
    category: "analytics",
    description: "נתוני API סדנאות",
    purpose: "מטמון נתוני סדנאות לשיפור ביצועים",
    retention: "24 שעות",
    required: false,
  },
  {
    name: "api_blog",
    category: "analytics",
    description: "נתוני API בלוג",
    purpose: "מטמון נתוני בלוג לשיפור ביצועים",
    retention: "24 שעות",
    required: false,
  },
  {
    name: "api_users",
    category: "analytics",
    description: "נתוני API משתמשים",
    purpose: "מטמון נתוני משתמשים לשיפור ביצועים",
    retention: "7 ימים",
    required: false,
  },

  // קוקיז שיווקיים (כרגע אין באתר)
  {
    name: "marketing_tracking",
    category: "marketing",
    description: "מעקב שיווקי",
    purpose: "מעקב אחר התנהגות משתמש לפרסום ממוקד",
    retention: "90 ימים",
    required: false,
  },
];

class CookieConsentService {
  private readonly CONSENT_KEY = "cookie_consent";
  private readonly VERSION = "1.0.0";

  /**
   * בדיקה אם יש הסכמה נוכחית
   */
  hasConsent(): boolean {
    return cookieService.has(this.CONSENT_KEY);
  }

  /**
   * קבלת הסכמה נוכחית
   */
  getConsent(): CookieConsent | null {
    try {
      // שימוש ישיר ב-Cookies במקום cookieService
      const cookieValue = Cookies.get(this.CONSENT_KEY);

      if (!cookieValue) {
        return null;
      }

      const consent = JSON.parse(cookieValue);

      return consent;
    } catch (error) {
            return null;
    }
  }

  /**
   * שמירת הסכמה חדשה
   */
  setConsent(consent: Partial<CookieConsent>): void {
    const fullConsent: CookieConsent = {
      essential: true, // תמיד true
      functional: consent.functional ?? false,
      analytics: consent.analytics ?? false,
      marketing: consent.marketing ?? false,
      timestamp: Date.now(),
      version: this.VERSION,
    };

    // שמירה ישירה ללא בדיקת הרשאה
    try {
      const cookieValue = JSON.stringify(fullConsent);

      // יצירת תאריך תפוגה לשנה מהיום
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);

      const cookieOptions = {
        expires: expirationDate, // תאריך מפורש לשנה מהיום
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax" as const,
        path: "/", // וידוא שהקוקיז זמין בכל הדפים
      };

      // שימוש ישיר ב-Cookies במקום cookieService
      Cookies.set(this.CONSENT_KEY, cookieValue, cookieOptions);
    } catch (error) {
          }
  }

  /**
   * עדכון הסכמה
   */
  updateConsent(updates: Partial<CookieConsent>): void {
    const currentConsent = this.getConsent();
    if (currentConsent) {
      const updatedConsent = {
        ...currentConsent,
        ...updates,
        timestamp: Date.now(),
        essential: true, // תמיד true
      };
      this.setConsent(updatedConsent);
    }
  }

  /**
   * בדיקה אם יש הרשאה לקטגוריה ספציפית
   */
  hasConsentForCategory(category: CookieCategory): boolean {
    const consent = this.getConsent();
    if (!consent) return false;

    // קוקיז חיוניים תמיד מותרים
    if (category === "essential") return true;

    return consent[category] === true;
  }

  /**
   * בדיקה אם יש הרשאה לקוקיז ספציפי
   */
  hasConsentForCookie(cookieName: string): boolean {
    // אם זה הקוקיז של ההסכמה עצמו, תמיד מותר
    if (cookieName === this.CONSENT_KEY) {
      return true;
    }

    const cookieDef = COOKIE_DEFINITIONS.find((c) => c.name === cookieName);
    if (!cookieDef) {
      return false;
    }

    // קוקיז חיוניים תמיד מותרים
    if (cookieDef.category === "essential") {
      return true;
    }

    return this.hasConsentForCategory(cookieDef.category);
  }

  /**
   * מחיקת כל הקוקיז שאין להם הרשאה
   */
  cleanupUnauthorizedCookies(): void {
    const consent = this.getConsent();
    if (!consent) return;

    const allCookies = cookieService.getAll();

    Object.keys(allCookies).forEach((cookieName) => {
      // דילוג על קוקיז חיוניים
      if (cookieName === this.CONSENT_KEY) return;

      const cookieDef = COOKIE_DEFINITIONS.find((c) => c.name === cookieName);
      if (cookieDef && !this.hasConsentForCategory(cookieDef.category)) {
        cookieService.remove(cookieName);
      }
    });
  }

  /**
   * קבלת רשימת קוקיז לפי קטגוריה
   */
  getCookiesByCategory(category: CookieCategory): CookieSettings[] {
    return COOKIE_DEFINITIONS.filter((cookie) => cookie.category === category);
  }

  /**
   * קבלת סטטיסטיקות הסכמה
   */
  getConsentStats(): {
    totalCookies: number;
    consentedCategories: number;
    totalCategories: number;
    lastUpdated: Date | null;
  } {
    const consent = this.getConsent();
    const totalCategories = 4; // essential, functional, analytics, marketing

    if (!consent) {
      return {
        totalCookies: COOKIE_DEFINITIONS.length,
        consentedCategories: 1, // רק essential
        totalCategories,
        lastUpdated: null,
      };
    }

    const consentedCategories = Object.values(consent).filter(Boolean).length;

    return {
      totalCookies: COOKIE_DEFINITIONS.length,
      consentedCategories,
      totalCategories,
      lastUpdated: new Date(consent.timestamp),
    };
  }

  /**
   * איפוס כל ההסכמות
   */
  resetConsent(): void {
    cookieService.remove(this.CONSENT_KEY);
  }

  /**
   * בדיקה אם צריך להציג באנר קוקיז
   */
  shouldShowBanner(): boolean {
    const consent = this.getConsent();

    // אם אין הסכמה בכלל
    if (!consent) {
      return true;
    }

    // אם ההסכמה לא תקפה (למשל, פג תוקף)
    const consentAge = Date.now() - consent.timestamp;
    const maxAge = 365 * 24 * 60 * 60 * 1000; // שנה
    if (consentAge > maxAge) {
      return true;
    }

    // אם יש עדכון בגרסה (רק אם ההסכמה לא פגה)
    if (this.hasVersionUpdate()) {
      return true;
    }

    return false;
  }

  /**
   * בדיקה אם יש עדכון בגרסה
   */
  hasVersionUpdate(): boolean {
    const consent = this.getConsent();
    if (!consent) return false;

    // אם אין גרסה בהסכמה, זה הסכמה ישנה
    if (!consent.version) return true;

    return consent.version !== this.VERSION;
  }

  /**
   * קבלת הודעת עדכון מדיניות
   */
  getPolicyUpdateMessage(): string {
    const consent = this.getConsent();
    if (!consent) return "";

    const lastUpdate = new Date(consent.timestamp);
    const daysSinceUpdate = Math.floor(
      (Date.now() - consent.timestamp) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceUpdate > 30) {
      return "עדכנו את מדיניות הקוקיז שלנו. אנא בדקו את ההגדרות החדשות.";
    }

    return "";
  }
}

// יצירת מופע גלובלי
export const cookieConsentService = new CookieConsentService();

export default CookieConsentService;
